<template>
  <div class="hero-section">
    <!-- Layered background elements -->
    <div class="hero-bg-gradient"></div>
    <div class="hero-bg-grid"></div>
    <div class="hero-glow hero-glow--1"></div>
    <div class="hero-glow hero-glow--2"></div>

    <!-- Stylized temple gopuram + city skyline silhouette (decorative) -->
    <svg class="hero-skyline" viewBox="0 0 560 280" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <g fill="#ffffff">
        <path d="M60 280 L60 200 L80 200 L80 180 L100 160 L120 180 L120 200 L140 200 L140 280 Z"/>
        <rect x="90" y="160" width="20" height="6"/>
        <rect x="95" y="155" width="10" height="4"/>
        <path d="M97 155 L100 145 L103 155 Z"/>
        <rect x="170" y="180" width="40" height="100"/>
        <rect x="220" y="140" width="50" height="140"/>
        <rect x="280" y="170" width="35" height="110"/>
        <rect x="325" y="120" width="60" height="160"/>
        <rect x="395" y="160" width="40" height="120"/>
        <rect x="445" y="100" width="55" height="180"/>
        <rect x="510" y="170" width="40" height="110"/>
      </g>
      <g fill="#0a1a4e" opacity="0.85">
        <rect x="178" y="195" width="6" height="6"/><rect x="190" y="195" width="6" height="6"/><rect x="178" y="210" width="6" height="6"/><rect x="190" y="210" width="6" height="6"/>
        <rect x="230" y="155" width="6" height="6"/><rect x="244" y="155" width="6" height="6"/><rect x="258" y="155" width="6" height="6"/><rect x="230" y="172" width="6" height="6"/><rect x="244" y="172" width="6" height="6"/><rect x="258" y="172" width="6" height="6"/>
        <rect x="335" y="135" width="6" height="6"/><rect x="350" y="135" width="6" height="6"/><rect x="365" y="135" width="6" height="6"/><rect x="335" y="155" width="6" height="6"/><rect x="350" y="155" width="6" height="6"/><rect x="365" y="155" width="6" height="6"/>
        <rect x="455" y="115" width="6" height="6"/><rect x="470" y="115" width="6" height="6"/><rect x="485" y="115" width="6" height="6"/><rect x="455" y="135" width="6" height="6"/><rect x="470" y="135" width="6" height="6"/><rect x="485" y="135" width="6" height="6"/>
      </g>
    </svg>

    <div class="hero-inner">
      <!-- Left content -->
      <div class="hero-left">
        <div class="hero-badge">
          <span class="hero-badge-dot"></span>
          <span>{{ heroBadgeText }}</span>
        </div>

        <h1 class="title">
          <span class="title-top">Your Indian Community Marketplace</span><span class="title-br"></span>
          <span class="title-accent">Rentals, Jobs &amp; Services &mdash; Free</span>
        </h1>
        <p class="hero-desc">
          {{ heroDescriptionText }}
        </p>

        <!-- Trust pill strip — low-friction reassurance near the CTA -->
        <div class="trust-pills" aria-label="Why trust Renants">
          <span class="trust-pill">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
              <path d="M20 6 9 17l-5-5"/>
            </svg>
            100% Free to Post
          </span>
          <span class="trust-pill">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
              <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
            </svg>
            Verified Community
          </span>
          <span class="trust-pill">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
              <rect x="2" y="5" width="20" height="14" rx="2"/>
              <line x1="2" y1="10" x2="22" y2="10"/>
              <line x1="5" y1="15" x2="9" y2="15"/>
            </svg>
            No Credit Card
          </span>
        </div>

        <!-- Search bar: What (free text, local match + AI fallback) + Where (location autocomplete) -->
        <div class="search-card" ref="searchCard">
          <div class="input-wrapper what-wrapper">
            <svg class="what-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>
            <input
              type="text"
              placeholder='Try "2 BHK apartment" or "IT jobs"'
              class="location-input what-input"
              v-model="whatText"
              @input="onWhatInput"
              @focus="onWhatInput"
              @keydown.enter="submitSearch"
              @keydown.escape="showWhatDropdown = false"
            />
            <transition name="fade">
              <div v-if="showWhatDropdown" class="what-dropdown">
                <div class="what-dropdown-scroll">
                  <template v-if="whatMatches.length">
                    <p class="what-group-label">Quick matches</p>
                    <div
                      v-for="(item, index) in whatMatches"
                      :key="'m' + index"
                      class="what-row"
                      @mousedown.prevent="selectWhatMatch(item)"
                    >
                      <span class="what-chip what-chip--cat" aria-hidden="true">
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/></svg>
                      </span>
                      <span class="what-row-main">
                        <span class="what-row-title">
                          <span v-for="(seg, si) in hl(item.label)" :key="si" :class="{ 'hl-match': seg.m }">{{ seg.t }}</span>
                        </span>
                        <span class="what-row-sub">{{ item.type === 'type1' ? 'Category' : 'in ' + item.typeLabel }}</span>
                      </span>
                    </div>
                  </template>
                  <template v-if="whatSuggest.listings.length">
                    <p class="what-group-label">Listings near you</p>
                    <div
                      v-for="(item, index) in whatSuggest.listings"
                      :key="'l' + index"
                      class="what-row"
                      @mousedown.prevent="selectListingSuggestion(item)"
                    >
                      <span class="what-chip what-chip--listing" aria-hidden="true">
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>
                      </span>
                      <span class="what-row-main">
                        <span class="what-row-title">
                          <span v-for="(seg, si) in hl(item.name)" :key="si" :class="{ 'hl-match': seg.m }">{{ seg.t }}</span>
                        </span>
                        <span class="what-row-sub">{{ listingSub(item) }}</span>
                      </span>
                    </div>
                  </template>
                  <template v-if="whatSuggest.deals.length">
                    <p class="what-group-label">Deals</p>
                    <div
                      v-for="(deal, index) in whatSuggest.deals"
                      :key="'d' + index"
                      class="what-row"
                      @mousedown.prevent="goToDeal(deal)"
                    >
                      <span class="what-chip what-chip--deal" aria-hidden="true">
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12.586 2.586A2 2 0 0 0 11.172 2H4a2 2 0 0 0-2 2v7.172a2 2 0 0 0 .586 1.414l8.704 8.704a2.426 2.426 0 0 0 3.42 0l6.58-6.58a2.426 2.426 0 0 0 0-3.42z"/><circle cx="7.5" cy="7.5" r="1"/></svg>
                      </span>
                      <span class="what-row-main">
                        <span class="what-row-title">
                          <span v-for="(seg, si) in hl(deal.title)" :key="si" :class="{ 'hl-match': seg.m }">{{ seg.t }}</span>
                        </span>
                        <span class="what-row-sub">Deal{{ deal.category ? ' · ' + humanize(deal.category) : '' }}</span>
                      </span>
                    </div>
                  </template>
                  <template v-if="whatSuggest.businesses.length">
                    <p class="what-group-label">Local businesses</p>
                    <div
                      v-for="(biz, index) in whatSuggest.businesses"
                      :key="'b' + index"
                      class="what-row"
                      @mousedown.prevent="goToBusiness(biz)"
                    >
                      <span class="what-chip what-chip--biz" aria-hidden="true">
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 9l1.5-5h15L21 9"/><path d="M3 9a3 3 0 0 0 6 0 3 3 0 0 0 6 0 3 3 0 0 0 6 0"/><path d="M5 11.5V21h14v-9.5"/><path d="M9 21v-6h6v6"/></svg>
                      </span>
                      <span class="what-row-main">
                        <span class="what-row-title">
                          <span v-for="(seg, si) in hl(biz.businessName)" :key="si" :class="{ 'hl-match': seg.m }">{{ seg.t }}</span>
                        </span>
                        <span class="what-row-sub">Business{{ biz.category ? ' · ' + humanize(biz.category) : '' }}</span>
                      </span>
                    </div>
                  </template>
                </div>
                <div class="what-ai-bar" @mousedown.prevent="submitSearch">
                  <span class="what-ai-spark" aria-hidden="true">✨</span>
                  <span class="what-ai-text">Search with AI for "<b>{{ whatText.trim() }}</b>"</span>
                  <span class="what-ai-enter">Enter ↵</span>
                </div>
              </div>
            </transition>
          </div>
          <div class="search-divider" aria-hidden="true"></div>
          <div class="input-wrapper where-wrapper">
            <img src="../icons/locationMobile.svg" class="location-icon" alt="Location" />
            <input
              type="text"
              placeholder="City or ZIP"
              class="location-input"
              v-model="userinput"
              @input="debouncedFetchSuggestions"
              @keydown.enter="submitSearch"
            />
            <transition name="fade">
              <ul v-if="suggestions.length" class="suggestions-list">
                <li
                  v-for="(suggestion, index) in suggestions"
                  :key="index"
                  @click="selectSuggestion(suggestion)"
                >
                  📍 {{ suggestion.description }}
                </li>
              </ul>
            </transition>
          </div>
          <button class="search-btn" @click="submitSearch">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" style="margin-right:6px"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>
            Search
          </button>
        </div>

        <!-- Stats row -->
        <div class="hero-stats">
          <div class="stat-item">
            <span class="stat-num">5,000+</span>
            <span class="stat-label">Active listings</span>
          </div>
          <div class="stat-divider"></div>
          <div class="stat-item">
            <span class="stat-num">50+</span>
            <span class="stat-label">Cities covered</span>
          </div>
          <div class="stat-divider"></div>
          <div class="stat-item">
            <span class="stat-num">Free</span>
            <span class="stat-label">To post & browse</span>
          </div>
        </div>
      </div>

      <!-- Right content: feature cards -->
      <div class="hero-right">
        <div class="hero-card" @click="filterByCategory('Rentals')">
          <div class="hero-card-icon-wrap" style="background: linear-gradient(135deg, rgba(99,179,237,0.2) 0%, rgba(59,130,246,0.15) 100%);">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#3b82f6" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M15 21v-8a1 1 0 0 0-1-1h-4a1 1 0 0 0-1 1v8"/>
              <path d="M3 10a2 2 0 0 1 .709-1.528l7-5.999a2 2 0 0 1 2.582 0l7 5.999A2 2 0 0 1 21 10v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
            </svg>
          </div>
          <div class="hero-card-body">
            <p class="hero-card-label">Rental Rooms</p>
            <p class="hero-card-sub">Find shared & private rooms near you</p>
          </div>
          <div class="hero-card-arrow">
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M5 12h14"/><path d="m12 5 7 7-7 7"/>
            </svg>
          </div>
        </div>
        <div class="hero-card" @click="filterByCategory('Jobs')">
          <div class="hero-card-icon-wrap" style="background: linear-gradient(135deg, rgba(34,197,94,0.2) 0%, rgba(22,163,74,0.15) 100%);">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#16a34a" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M16 20V4a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"/>
              <rect width="20" height="14" x="2" y="6" rx="2"/>
            </svg>
          </div>
          <div class="hero-card-body">
            <p class="hero-card-label">Job Posts</p>
            <p class="hero-card-sub">Browse local employment opportunities</p>
          </div>
          <div class="hero-card-arrow">
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M5 12h14"/><path d="m12 5 7 7-7 7"/>
            </svg>
          </div>
        </div>
        <div class="hero-card" @click="filterByCategory('Sale')">
          <div class="hero-card-icon-wrap" style="background: linear-gradient(135deg, rgba(251,191,36,0.2) 0%, rgba(245,158,11,0.15) 100%);">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#f59e0b" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <circle cx="8" cy="21" r="1"/>
              <circle cx="19" cy="21" r="1"/>
              <path d="M2.05 2.05h2l2.66 12.42a2 2 0 0 0 2 1.58h9.78a2 2 0 0 0 1.95-1.57l1.65-7.43H5.12"/>
            </svg>
          </div>
          <div class="hero-card-body">
            <p class="hero-card-label">Items for Sale</p>
            <p class="hero-card-sub">Buy & sell locally with ease</p>
          </div>
          <div class="hero-card-arrow">
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M5 12h14"/><path d="m12 5 7 7-7 7"/>
            </svg>
          </div>
        </div>
        <div class="hero-card" @click="filterByCategory('Events')">
          <div class="hero-card-icon-wrap" style="background: linear-gradient(135deg, rgba(236,72,153,0.2) 0%, rgba(219,39,119,0.15) 100%);">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#db2777" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M8 2v4"/>
              <path d="M16 2v4"/>
              <rect width="18" height="18" x="3" y="4" rx="2"/>
              <path d="M3 10h18"/>
              <path d="m9 16 2 2 4-4"/>
            </svg>
          </div>
          <div class="hero-card-body">
            <p class="hero-card-label">Events</p>
            <p class="hero-card-sub">Discover what's happening near you</p>
          </div>
          <div class="hero-card-arrow">
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M5 12h14"/><path d="m12 5 7 7-7 7"/>
            </svg>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import axios from "axios";
import { EventBus } from "../../services/EventBus.js";
import { trackPageVisit } from "../../utils/trackPageVisit.js";
import { getIPLocation } from "../../services/ipLocationService.js";
import { matchServiceTypes, resolveExactServiceType } from "../../utils/serviceTypeMatcher.js";
import { trackInteraction } from "../../utils/trackInteraction.js";

export default {
  data() {
    return {
      // Badge fields — pinned to the user's IP-detected location.
      // Intentionally decoupled from EventBus.selectedLocation (the active
      // filter), which may be set to a state-only label by AI search etc.
      detectedCity: "",
      detectedState: "",
      userinput: "",
      suggestions: [],
      latitude: null,
      longitude: null,
      selectedLat: null,
      selectedLng: null,
      _debounceTimer: null,
      // "What" field (free text with local category matching + AI fallback)
      whatText: "",
      whatMatches: [],
      showWhatDropdown: false,
      _whatDebounce: null,
      // Live data-backed suggestions (listing names near you, deals, businesses)
      whatSuggest: { listings: [], deals: [], businesses: [] },
      _suggestSeq: 0,
    };
  },

  computed: {
    heroBadgeText() {
      const city = this.detectedCity;
      const state = this.detectedState;

      if (city && state) {
        return `Serving communities in ${city}, ${state}`;
      }

      if (city) {
        return `Serving communities in ${city}`;
      }

      return "Serving communities across the United States";
    },

    heroDescriptionText() {
      const city = this.detectedCity;
      const state = this.detectedState;

      if (city && state) {
        return `Browse listings or post your own near ${city}, ${state} — completely free, always.`;
      }

      if (city) {
        return `Browse listings or post your own near ${city} — completely free, always.`;
      }

      return "Browse listings or post your own near you — completely free, always.";
    },
  },

  methods: {
    parseCityState(label) {
      if (!label || typeof label !== "string") {
        return { city: "", state: "" };
      }

      const parts = label
        .split(",")
        .map((part) => part.trim())
        .filter(Boolean);

      return {
        city: parts[0] || "",
        state: parts[1] || "",
      };
    },

    // Normalize a state name to its 2-letter postal code. Returns the input
    // unchanged if it's already 2 letters or can't be matched, so callers
    // never end up with an empty state.
    toStateCode(value) {
      if (!value || typeof value !== "string") return "";
      const v = value.trim();
      if (v.length === 2) return v.toUpperCase();
      const map = {
        "alabama":"AL","alaska":"AK","arizona":"AZ","arkansas":"AR","california":"CA",
        "colorado":"CO","connecticut":"CT","delaware":"DE","florida":"FL","georgia":"GA",
        "hawaii":"HI","idaho":"ID","illinois":"IL","indiana":"IN","iowa":"IA","kansas":"KS",
        "kentucky":"KY","louisiana":"LA","maine":"ME","maryland":"MD","massachusetts":"MA",
        "michigan":"MI","minnesota":"MN","mississippi":"MS","missouri":"MO","montana":"MT",
        "nebraska":"NE","nevada":"NV","new hampshire":"NH","new jersey":"NJ","new mexico":"NM",
        "new york":"NY","north carolina":"NC","north dakota":"ND","ohio":"OH","oklahoma":"OK",
        "oregon":"OR","pennsylvania":"PA","rhode island":"RI","south carolina":"SC",
        "south dakota":"SD","tennessee":"TN","texas":"TX","utah":"UT","vermont":"VT",
        "virginia":"VA","washington":"WA","west virginia":"WV","wisconsin":"WI","wyoming":"WY",
        "district of columbia":"DC",
      };
      return map[v.toLowerCase()] || v;
    },

    filterByCategory(category) {
      // Clear AI search so normal filtering takes over
      EventBus.clearSearchQuery();
      // Set the selected service type in EventBus
      EventBus.setServiceType(category);
      // Clear any subtype filter
      EventBus.selectedSubtype = "";

      // Pass the location from hero search input to advanced filter
      // Use coords from autocomplete pick, or IP/geo detection, or existing EventBus
      const lat = this.selectedLat || this.latitude
        || (typeof EventBus.selectedLocation === 'object' ? EventBus.selectedLocation.lat : null);
      const lng = this.selectedLng || this.longitude
        || (typeof EventBus.selectedLocation === 'object' ? EventBus.selectedLocation.lng : null);
      if (this.userinput) {
        if (lat && lng) {
          EventBus.selectedLocation = { lat, lng, label: this.userinput };
        } else {
          EventBus.selectedLocation = this.userinput;
        }
      }
      
      // Set default nearby distance (30 miles) to match AI-search default
      EventBus.selectedDistance = 30;

      // Open the advanced search panel
      EventBus.openAdvanceFilter = true;

      this.scrollToResults();
    },

    scrollToResults() {
      this.$nextTick(() => {
        const filterSection = document.querySelector('.filter-sec');
        if (filterSection) {
          filterSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
        } else {
          // Fallback: scroll to a fixed position
          window.scrollTo({ top: 500, behavior: 'smooth' });
        }
      });
    },

    // ── "What" field: local category matching with AI fallback ──────────────
    onWhatInput() {
      clearTimeout(this._whatDebounce);
      this._whatDebounce = setTimeout(() => {
        this.whatMatches = matchServiceTypes(this.whatText);
        this.showWhatDropdown = this.whatText.trim().length >= 2;
        this.fetchWhatSuggestions();
      }, 200);
    },

    // Best-effort typeahead against real data: listing names near the user,
    // active deal titles, and directory business names. A stale-response guard
    // (monotonic seq) keeps fast typing from showing out-of-date suggestions.
    async fetchWhatSuggestions() {
      const q = this.whatText.trim();
      if (q.length < 2) {
        this.whatSuggest = { listings: [], deals: [], businesses: [] };
        return;
      }
      const seq = ++this._suggestSeq;
      try {
        const { lat, lng } = this.resolveWhereCoords();
        const { data } = await axios.post("/api/search/suggest", { q, lat, lng });
        if (seq !== this._suggestSeq) return;
        if (this.whatText.trim().length >= 2) {
          this.whatSuggest = {
            listings: data.listings || [],
            deals: data.deals || [],
            businesses: data.businesses || [],
          };
        }
      } catch {
        /* suggestions are best-effort — never block the search UI */
      }
    },

    selectWhatMatch(item) {
      this.whatText = item.label;
      this.showWhatDropdown = false;
      this.dispatchStructured(item);
    },

    // Split `text` into segments, marking the part that matches the typed
    // query so the dropdown can bold it. Returns [{t, m}] where m = matched.
    hl(text) {
      const t = String(text || "");
      const q = this.whatText.trim().toLowerCase();
      if (q.length < 2) return [{ t, m: false }];
      const idx = t.toLowerCase().indexOf(q);
      if (idx === -1) return [{ t, m: false }];
      return [
        { t: t.slice(0, idx), m: false },
        { t: t.slice(idx, idx + q.length), m: true },
        { t: t.slice(idx + q.length), m: false },
      ].filter((seg) => seg.t);
    },

    listingSub(item) {
      const type = item.serviceType2 || item.serviceType1 || "Listing";
      if (Number.isFinite(item.distance)) {
        const miles = Math.round(item.distance / 1609.34);
        return `${type} · ${miles <= 1 ? "under 1" : miles} mi away`;
      }
      return type;
    },

    // "food_dining" → "Food dining", "restaurants" → "Restaurants"
    humanize(value) {
      const s = String(value || "").replace(/_/g, " ").trim();
      return s ? s.charAt(0).toUpperCase() + s.slice(1) : "";
    },

    // A concrete listing name was picked — run the free-text search for it so
    // the grid shows that listing plus similar ones nearby.
    selectListingSuggestion(item) {
      this.whatText = item.name;
      this.showWhatDropdown = false;
      this.submitSearch();
    },

    goToDeal(deal) {
      this.showWhatDropdown = false;
      this.$router.push(`/deals/${deal.slug}`);
    },

    goToBusiness(biz) {
      this.showWhatDropdown = false;
      this.$router.push(`/more/${biz.category}/${biz.slug}`);
    },

    // Resolve "where" coords the same way filterByCategory/inputLocation do:
    // autocomplete pick → IP/geo detection → existing EventBus object.
    resolveWhereCoords() {
      const lat = this.selectedLat || this.latitude
        || (typeof EventBus.selectedLocation === 'object' ? EventBus.selectedLocation.lat : null);
      const lng = this.selectedLng || this.longitude
        || (typeof EventBus.selectedLocation === 'object' ? EventBus.selectedLocation.lng : null);
      return { lat, lng };
    },

    submitSearch() {
      this.showWhatDropdown = false;
      this.suggestions = [];
      const what = this.whatText.trim();
      const whereLabel = this.userinput.trim();

      // Every branch below is the SAME user action — the hero Search button —
      // so all three report as `hero-search` and `meta.mode` says which way it
      // went. Tracking only Case C (as this used to) hid the majority of hero
      // searches from the admin Clicks report.
      const trackSubmit = (mode, extra = {}) => {
        const { lat, lng } = this.resolveWhereCoords();
        trackInteraction('search', 'hero-search', {
          label: what,
          extra: { source: 'hero', mode, where: whereLabel || undefined, hasCoords: !!(lat && lng), ...extra },
        });
      };

      // Case A: no "what" → plain location search (today's behavior)
      if (!what) {
        if (!whereLabel) return;
        trackSubmit('location-only');
        this.inputLocation();
        return;
      }

      // Case B: "what" is a known category/subtype → structured search, no AI
      const exact = resolveExactServiceType(what);
      if (exact) {
        trackSubmit('structured', { matchedType: exact.value, matchedLevel: exact.type });
        this.dispatchStructured(exact);
        return;
      }

      // Case C: free text → AI search. An explicitly geocoded "where" rides
      // along so the backend can geo-filter; a bare text "where" degrades to
      // being part of the query for the parser to extract.
      trackSubmit('ai');
      EventBus.userInteracted = true;
      const { lat, lng } = this.resolveWhereCoords();
      if (lat && lng) {
        EventBus.setSearchQuery(what, { lat, lng, label: whereLabel });
      } else if (whereLabel) {
        EventBus.setSearchQuery(`${what} in ${whereLabel}`);
      } else {
        EventBus.setSearchQuery(what);
      }
      this.scrollToResults();
    },

    dispatchStructured(match) {
      // KEY RULE: non-AI path clears the AI query first (see CLAUDE.md)
      EventBus.clearSearchQuery();
      if (match.type === "type1") {
        EventBus.setServiceType(match.value);
      } else {
        EventBus.setSubtype(match.value);
      }
      // Attach "where" only when present (mirrors filterByCategory)
      if (this.userinput.trim()) {
        const { lat, lng } = this.resolveWhereCoords();
        EventBus.selectedLocation = lat && lng
          ? { lat, lng, label: this.userinput.trim() }
          : this.userinput.trim();
      }
      EventBus.selectedDistance = 30;
      EventBus.openAdvanceFilter = true;
      this.scrollToResults();
    },

    handleHeroOutsideClick(e) {
      const card = this.$refs.searchCard;
      if (card && !card.contains(e.target)) {
        this.showWhatDropdown = false;
      }
    },
    // Logged-in visits go to the admin "Authenticated Users" tab, guests to
    // "Unauthenticated Users" — this used to log everyone as a guest.
    trackVisitor() {
      trackPageVisit("Dashboard");
    },
    inputLocation() {
      // Clear AI search so location-based filtering takes over
      EventBus.clearSearchQuery();
      const lat = this.selectedLat || this.latitude
        || (typeof EventBus.selectedLocation === 'object' ? EventBus.selectedLocation.lat : null);
      const lng = this.selectedLng || this.longitude
        || (typeof EventBus.selectedLocation === 'object' ? EventBus.selectedLocation.lng : null);
      if (lat && lng) {
        EventBus.setLocation({ lat, lng, label: this.userinput });
      } else {
        EventBus.setLocation(this.userinput);
      }
      window.scrollTo({ top: 500, behavior: "smooth" });
    },

//     async loadUserLocation() {
//       // 1) Try loading saved location but DO NOT return early
//       const savedLocation = localStorage.getItem("userLocation");

//       if (savedLocation) {
//         try {
//           this.locationName = JSON.parse(savedLocation);
//           this.useDetectedLocation();
//         } catch (err) {
//           console.warn("Invalid saved location format");
//         }
//       }

//       // 2) Try geolocation normally
//       try {
//         const perm = await navigator.permissions?.query({
//           name: "geolocation",
//         });

//         if (!perm || perm.state === "granted" || perm.state === "prompt") {
//           this.getLocation();
//           return; // allow geolocation to run
//         }
//       } catch (err) {
//         // Permissions API unsupported → still attempt geolocation
//         this.getLocation();
//         return;
//       }

//       // 3) If geolocation permission denied → use IP
//       this.loadLocationFromIP();
//     },

//     useDetectedLocation() {
//       if (this.locationName && this.locationName.city) {
//         this.userinput = this.locationName.city;
//         console.log("user input", this.userinput);
//         this.suggestions = [];
//       }
//     },

//     getLocation() {
//       if (navigator.geolocation) {
//         navigator.geolocation.getCurrentPosition(this.success, this.error);
//       } else {
//         console.log("Geolocation not supported");
//       }
//     },

//     async success(position) {
//       try {
//         this.latitude = position.coords.latitude;
//         this.longitude = position.coords.longitude;

//         const { data: address } = await axios.post("/api/reverse-geocode", {
//           latitude: this.latitude,
//           longitude: this.longitude,
//         });

//         this.locationName = {
//           city: address.city,
//           address: address.fullAddress,
//         };

//         // ⭐ Auto-fill input box with detected city
//         this.userinput = address.city || address.fullAddress;

//         const locationData = {
//           ...address,
//           lat: this.latitude,
//           lng: this.longitude,
//           fullAddress: address.fullAddress,
//         };
//         this.useDetectedLocation();
//         localStorage.setItem("userLocation", JSON.stringify(locationData));
//       } catch (error) {
//         this.userinput = "";
//         // console.error("Reverse geocode error:", error);
//         await this.loadLocationFromIP();
//       }
//     },

//     error(err) {
//       console.error("Geolocation error:", err.message);
//       this.loadLocationFromIP();
//     },

//     async loadLocationFromIP() {
//       try {
//         const res = await fetch("https://ipwho.is/");
//         const geo = await res.json();

//         if (!geo.success) throw new Error("IP lookup failed");

//         this.locationName = {
//           city: geo.city,
//           address: `${geo.city}, ${geo.region}, ${geo.country}`,
//         };

//         this.userinput = geo.city;

//         localStorage.setItem(
//           "userLocation",
//           JSON.stringify({
//             city: geo.city,
//             region: geo.region,
//             country: geo.country,
//             lat: geo.latitude,
//             lng: geo.longitude,
//             fullAddress: `${geo.city}, ${geo.region}, ${geo.country}`,
//           })
//         );

//         this.useDetectedLocation();
//       } catch (error) {
//         console.error("IP Geolocation failed:", error);
//       }
//     },
// async  getFallbackLatLng() {
//   try {
//     const res = await fetch("https://ipwho.is/");
//     const geo = await res.json();
//     if (geo.success) {
//       return {
//         lat: geo.latitude,
//         lng: geo.longitude
//       };
//     }
//   } catch (e) {
//     console.warn("IP geolocation failed");
//   }

//   return null; 
// },

    selectSuggestion(suggestion) {
      this.userinput = suggestion.description;
      this.selectedLat = suggestion._lat || null;
      this.selectedLng = suggestion._lng || null;
      this.suggestions = [];
    },
    debouncedFetchSuggestions() {
      // Clear cached coords when user types a new location
      this.selectedLat = null;
      this.selectedLng = null;
      clearTimeout(this._debounceTimer);
      this._debounceTimer = setTimeout(() => this.fetchSuggestions(), 500);
    },
    async fetchSuggestions() {
      const input = this.userinput.trim();

      if (!input) {
        this.suggestions = [];
        return;
      }

      if (input.length < 3) {
        this.suggestions = [];
        return;
      }

      try {
        const response = await axios.post("/api/places/autocomplete", {
          input: input,
        });

        if (!this.userinput.trim()) {
          this.suggestions = [];
        } else {
          this.suggestions = response.data;
        }
      } catch (error) {
        console.error("Error fetching suggestions:", error.message || error);
        this.suggestions = [];
      }
    },
  async loadUserLocation() {
    // The badge is pinned to the user's IP-detected location, so we always
    // run the IP lookup regardless of what EventBus.selectedLocation currently
    // holds. getIPLocation() is a memoized singleton, so this piggybacks on
    // the request Views.vue already kicked off — no extra network call.
    await this.loadLocationFromIP();
  },

  async onGeoSuccess(position) {
    try {
      await this.loadLocationFromIP();
    } catch {
      await this.loadLocationFromIP();
    }
  },

async onGeoError(err) {
  if (err.code === err.PERMISSION_DENIED) {
    console.log("User denied location → skipping detection");
    return; // ⛔ stop here
  }
},

  // async loadLocationFromIP() {
  //   try {
  //     const res = await fetch("https://ipwho.is/");
  //     const geo = await res.json();

  //     if (!geo.success) throw new Error("IP lookup failed");

  //     const location = {
  //       city: geo.city,
  //       region: geo.region,
  //       country: geo.country,
  //       lat: geo.latitude,
  //       lng: geo.longitude,
  //       fullAddress: `${geo.city}, ${geo.region}, ${geo.country}`,
  //     };

  //     this.applyLocation(location);
  //     console.log("location from ip", location);
  //     localStorage.setItem("userLocation", JSON.stringify(location));
  //   } catch (err) {
  //     console.error("IP Geolocation failed:", err);
  //   }
  // },

  async loadLocationFromIP() {
    // Uses the shared singleton — no extra network call if Views.vue already fetched it.
    const geo = await getIPLocation();
    if (!geo) return;

    this.applyLocation(geo);
    localStorage.setItem("userLocation", JSON.stringify(geo));
  },


  applyLocation(location) {
    const derived = this.parseCityState(location.fullAddress || location.label || "");

    const city = location.city || derived.city || "";
    // region_code is preferred (already 2 letters); fall back to region with abbreviation.
    const stateRaw = location.region_code || location.region || location.state || derived.state || "";
    const stateCode = this.toStateCode(stateRaw);

    // Badge fields — these stay pinned to the IP-detected location for the
    // lifetime of the page. Filter changes never overwrite them.
    this.detectedCity  = city;
    this.detectedState = stateCode;

    this.latitude  = location.lat;
    this.longitude = location.lng;

    // Include state in the search-input value so downstream geocoding doesn't
    // resolve to the wrong city (e.g. "Somerville, NJ" not bare "Somerville"
    // which defaults to MA). Don't clobber an existing userinput — the
    // EventBus watcher (filter state) is the authoritative source for that.
    const displayLabel = city && stateCode ? `${city}, ${stateCode}` : city;
    if (!this.userinput) {
      this.userinput = displayLabel;
    }
    this.suggestions = [];

    // Sync to EventBus so AdvanceFilter headline and the listing also update.
    // Pass coords when available to skip re-geocoding entirely.
    if (!EventBus.selectedLocation) {
      if (location.lat && location.lng) {
        EventBus.selectedLocation = { lat: location.lat, lng: location.lng, label: displayLabel };
      } else {
        EventBus.selectedLocation = displayLabel;
      }
    }
  },

  },

  mounted() {
    this.trackVisitor();
    document.addEventListener("click", this.handleHeroOutsideClick);

    // Keep the hero search input in sync with EventBus.selectedLocation (the
    // active filter). Crucially this only updates `userinput` — the badge
    // (detectedCity/detectedState) is owned by IP geolocation and must not
    // be downgraded when filters set a less-specific label like "New Jersey".
    this.$watch(
      () => EventBus.selectedLocation,
      (newLoc) => {
        if (!newLoc) return;
        if (typeof newLoc === 'string' && newLoc) {
          this.userinput = newLoc;
        } else if (typeof newLoc === 'object' && newLoc.label) {
          this.userinput = newLoc.label;
        }
      },
      { immediate: true }  // apply synchronously if EventBus is already populated
    );

    this.loadUserLocation();
  },

  beforeUnmount() {
    document.removeEventListener("click", this.handleHeroOutsideClick);
    clearTimeout(this._whatDebounce);
    clearTimeout(this._debounceTimer);
  },
};
</script>

<style scoped>
/* ══════════════════════════════════════════════════════
   HERO SECTION
══════════════════════════════════════════════════════ */
.hero-section {
  width: 100%;
  background: #0a1a4e;
  position: relative;
  overflow-x: clip;
  overflow-y: visible;
  min-height: 320px;
  display: flex;
  align-items: center;
}

/* Rich layered gradient */
.hero-bg-gradient {
  position: absolute;
  inset: 0;
  background: linear-gradient(135deg, #0a1a4e 0%, #0d2880 40%, #1a3fa8 70%, #0f2060 100%);
  pointer-events: none;
}

/* Subtle dot grid */
.hero-bg-grid {
  position: absolute;
  inset: 0;
  background-image: radial-gradient(circle, rgba(255,255,255,0.06) 1px, transparent 1px);
  background-size: 28px 28px;
  pointer-events: none;
}

/* Soft glow orbs */
.hero-glow {
  position: absolute;
  border-radius: 50%;
  pointer-events: none;
  filter: blur(80px);
}

.hero-glow--1 {
  width: 500px;
  height: 500px;
  background: rgba(100, 149, 255, 0.18);
  top: -150px;
  right: -80px;
}

.hero-glow--2 {
  width: 350px;
  height: 350px;
  background: rgba(52, 103, 213, 0.20);
  bottom: -100px;
  left: 20%;
}

/* Decorative skyline silhouette behind the right-side cards */
.hero-skyline {
  position: absolute;
  right: 0;
  bottom: 0;
  width: 560px;
  height: 280px;
  opacity: 0.22;
  pointer-events: none;
  z-index: 0;
}
@media (max-width: 900px) {
  .hero-skyline { display: none; }
}

/* ── Inner layout ── */
.hero-inner {
  position: relative;
  z-index: 1;
  max-width: 1280px;
  width: 100%;
  margin: 0 auto;
  padding: 32px 48px;
  display: flex;
  align-items: center;
  gap: 56px;
  box-sizing: border-box;
}

/* ── Left column ── */
.hero-left {
  flex: 1;
  min-width: 0;
  color: #fff;
}

/* Badge */
.hero-badge {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: rgba(255,255,255,0.10);
  border: 1px solid rgba(255,255,255,0.18);
  border-radius: 999px;
  padding: 6px 16px 6px 10px;
  font-size: 13px;
  font-family: var(--font-primary);
  font-weight: 500;
  color: rgba(255,255,255,0.85);
  margin-bottom: 14px;
  letter-spacing: 0.01em;
}

.hero-badge-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #4ade80;
  box-shadow: 0 0 8px rgba(74,222,128,0.7);
  flex-shrink: 0;
  animation: pulse-dot 2s infinite;
}

@keyframes pulse-dot {
  0%, 100% { box-shadow: 0 0 6px rgba(74,222,128,0.7); }
  50% { box-shadow: 0 0 14px rgba(74,222,128,0.9); }
}

/* Title line-break helper – shows on desktop, hidden on mobile */
.title-br {
  display: block;
}

/* Headline */
.title {
  font-family: var(--font-primary);
  font-size: 46px;
  font-weight: 800;
  line-height: 1.12;
  color: #fff;
  margin: 0 0 10px;
  letter-spacing: -0.02em;
}

.title-top {
  font-size: 34px;
  font-weight: 700;
  letter-spacing: -0.01em;
}

.title-accent {
  background: linear-gradient(90deg, #7eb3ff, #a78bfa);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.hero-desc {
  font-family: var(--font-primary);
  font-size: 17px;
  color: rgba(255,255,255,0.68);
  margin: 0 0 20px;
  line-height: 1.55;
  max-width: 520px;
}

/* ── Trust pills ── subtle reassurance row above the search CTA */
.trust-pills {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin: 0 0 18px;
}

.trust-pill {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.18);
  backdrop-filter: blur(6px);
  -webkit-backdrop-filter: blur(6px);
  font-family: var(--font-primary);
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.01em;
  color: rgba(255, 255, 255, 0.92);
  white-space: nowrap;
  user-select: none;
  transition: background 0.15s ease, border-color 0.15s ease;
}

.trust-pill:hover {
  background: rgba(255, 255, 255, 0.13);
  border-color: rgba(255, 255, 255, 0.28);
}

.trust-pill svg {
  color: #4ade80; /* green-400 — signal success without shouting */
  flex-shrink: 0;
}

/* ── Search card ── */
.search-card {
  display: flex;
  align-items: center;
  gap: 0;
  margin-bottom: 16px;
  background: #ffffff;
  border-radius: 14px;
  padding: 6px 6px 6px 0;
  box-shadow: 0 8px 32px rgba(0,0,0,0.22), 0 2px 8px rgba(0,0,0,0.12);
  overflow: visible;
}

.input-wrapper {
  position: relative;
  flex: 1;
}

.location-icon {
  position: absolute;
  top: 50%;
  left: 16px;
  transform: translateY(-50%);
  width: 18px;
  height: 18px;
  opacity: 0.45;
  pointer-events: none;
}

.location-input {
  width: 100%;
  padding: 13px 14px 13px 46px;
  border: none;
  font-size: 15px;
  font-family: var(--font-primary);
  background: transparent;
  color: #111827;
  outline: none;
  box-sizing: border-box;
  border-radius: 10px 0 0 10px;
}

.location-input::placeholder {
  color: #9ca3af;
}

.search-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 12px 24px;
  border-radius: 10px;
  background: linear-gradient(135deg, #3467d5 0%, #1a3fa8 100%);
  color: #ffffff;
  font-weight: 700;
  font-size: 15px;
  font-family: var(--font-primary);
  border: none;
  cursor: pointer;
  white-space: nowrap;
  box-shadow: 0 2px 12px rgba(52,103,213,0.4);
  transition: transform 0.15s, box-shadow 0.15s, opacity 0.15s;
  flex-shrink: 0;
}

.search-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 6px 18px rgba(52,103,213,0.55);
  opacity: 0.95;
}

/* ── Dual-input: What + Where ── */
.what-wrapper {
  flex: 1.2;
}

.where-wrapper {
  flex: 1;
}

.what-icon {
  position: absolute;
  top: 50%;
  left: 16px;
  transform: translateY(-50%);
  color: #9ca3af;
  pointer-events: none;
}

.what-input {
  padding-left: 44px;
}

.search-divider {
  width: 1px;
  height: 28px;
  background: #e5e7eb;
  flex-shrink: 0;
}

/* Dropdown card: scrollable results + AI action bar pinned at the bottom */
.what-dropdown {
  position: absolute;
  top: calc(100% + 8px);
  left: 0;
  width: 100%;
  min-width: 300px;
  background: #ffffff;
  border: 1px solid #e8ebf3;
  border-radius: 14px;
  box-shadow: 0 12px 32px rgba(15, 32, 96, 0.16), 0 2px 8px rgba(15, 32, 96, 0.08);
  z-index: 2000;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.what-dropdown-scroll {
  max-height: 300px;
  overflow-y: auto;
  overscroll-behavior: contain;
  padding: 4px 0 6px;
}

.what-group-label {
  padding: 10px 14px 4px;
  margin: 0;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #9aa3b2;
}

.what-row {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 14px;
  cursor: pointer;
  transition: background 0.12s;
}

.what-row:hover {
  background: #f4f7ff;
}

.what-chip {
  width: 30px;
  height: 30px;
  border-radius: 9px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.what-chip--cat { background: #eef2f7; color: #5b6472; }
.what-chip--listing { background: #e8f0fe; color: #3467d5; }
.what-chip--deal { background: #fef3c7; color: #b45309; }
.what-chip--biz { background: #f3e8ff; color: #7c3aed; }

.what-row-main {
  display: flex;
  flex-direction: column;
  min-width: 0;
  flex: 1;
}

.what-row-title {
  font-size: 14px;
  color: #111827;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.hl-match {
  font-weight: 700;
  color: #1a3fa8;
}

.what-row-sub {
  font-size: 12px;
  color: #8b93a2;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.what-ai-bar {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 11px 14px;
  border-top: 1px solid #eef0f5;
  background: #fbfaff;
  color: #4f46b8;
  font-size: 14px;
  cursor: pointer;
  flex-shrink: 0;
  transition: background 0.12s;
}

.what-ai-bar:hover {
  background: #f3f0ff;
}

.what-ai-spark {
  flex-shrink: 0;
}

.what-ai-text {
  flex: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.what-ai-text b {
  font-weight: 700;
}

.what-ai-enter {
  font-size: 11px;
  font-weight: 500;
  color: #8b93a2;
  border: 1px solid #e2e6ef;
  border-radius: 6px;
  padding: 2px 7px;
  flex-shrink: 0;
}

/* ── Stats row ── */
.hero-stats {
  display: flex;
  align-items: center;
  gap: 0;
}

.stat-item {
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 0 20px 0 0;
}

.stat-item:first-child {
  padding-left: 0;
}

.stat-num {
  font-family: var(--font-primary);
  font-size: 22px;
  font-weight: 800;
  color: #fff;
  line-height: 1;
}

.stat-label {
  font-family: var(--font-primary);
  font-size: 12px;
  color: rgba(255,255,255,0.55);
  font-weight: 400;
  white-space: nowrap;
}

.stat-divider {
  width: 1px;
  height: 32px;
  background: rgba(255,255,255,0.18);
  margin: 0 20px 0 0;
  flex-shrink: 0;
}

/* ── Right column: feature cards ── */
.hero-right {
  flex: 0 0 310px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.hero-card {
  background: rgba(255,255,255,0.08);
  border: 1px solid rgba(255,255,255,0.14);
  border-radius: 14px;
  padding: 16px 18px;
  display: flex;
  align-items: center;
  gap: 14px;
  backdrop-filter: blur(12px);
  transition: background 0.2s, transform 0.2s, border-color 0.2s, box-shadow 0.2s;
  cursor: pointer;
  user-select: none;
}

.hero-card:hover {
  background: rgba(255,255,255,0.14);
  border-color: rgba(255,255,255,0.25);
  transform: translateX(4px);
  box-shadow: 0 4px 20px rgba(0,0,0,0.15);
}

.hero-card:active {
  transform: translateX(2px) scale(0.98);
}

.hero-card-icon-wrap {
  width: 44px;
  height: 44px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.hero-card:hover .hero-card-icon-wrap {
  transform: scale(1.05);
  box-shadow: 0 4px 12px rgba(0,0,0,0.15);
}

.hero-card-icon-wrap svg {
  width: 24px;
  height: 24px;
}

.hero-card-body {
  flex: 1;
  min-width: 0;
}

.hero-card-label {
  font-family: var(--font-primary);
  font-size: 14px;
  font-weight: 700;
  color: #fff;
  margin: 0 0 3px;
}

.hero-card-sub {
  font-family: var(--font-primary);
  font-size: 12px;
  color: rgba(255,255,255,0.58);
  margin: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.hero-card-arrow {
  display: flex;
  align-items: center;
  justify-content: center;
  color: rgba(255,255,255,0.35);
  flex-shrink: 0;
  transition: color 0.2s, transform 0.2s;
}

.hero-card-arrow svg {
  width: 20px;
  height: 20px;
}

.hero-card:hover .hero-card-arrow {
  color: rgba(255,255,255,0.85);
  transform: translateX(4px);
}

/* ── Suggestions dropdown ── */
.suggestions-list {
  position: absolute;
  top: calc(100% + 8px);
  left: 0;
  width: 100%;
  max-height: 250px;
  overflow-y: auto;
  background: #fff;
  color: #111;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  box-shadow: 0 8px 24px rgba(0,0,0,0.14);
  z-index: 2000;
  list-style: none;
  margin: 0;
  padding: 6px 0;
}

.suggestions-list li {
  padding: 11px 16px;
  font-size: 14px;
  font-family: var(--font-primary);
  cursor: pointer;
  color: #374151;
  transition: background 0.15s;
}

.suggestions-list li:hover {
  background: #f0f4ff;
  color: var(--primary-color, #3467d5);
}

/* fade transition */
.fade-enter-active, .fade-leave-active { transition: opacity 0.18s; }
.fade-enter-from, .fade-leave-to { opacity: 0; }

/* ══════════════════════════════════════════════════════
   RESPONSIVE
══════════════════════════════════════════════════════ */
@media (max-width: 960px) {
  .hero-right {
    display: none;
  }
  .hero-inner {
    padding: 50px 32px;
  }
  .title {
    font-size: 38px;
  }
  .title-top {
    font-size: 28px;
  }
}

@media (max-width: 640px) {
  /* Compact height so a listing card peeks below the fold */
  .hero-section {
    min-height: unset;
  }
  .hero-inner {
    padding: 24px 16px 28px;
    gap: 0;
  }

  /* Hide the badge on very small screens to save vertical space */
  .hero-badge {
    display: none;
  }

  /* Tighter, smaller headline */
  .title {
    font-size: 24px;
    line-height: 1.2;
    margin-bottom: 10px;
  }

  /* Keep line break between title-top and accent on mobile */
  .title-br {
    display: block;
  }

  .title-top {
    font-size: 20px;
    font-weight: 600;
    display: block;
    margin-bottom: 4px;
  }

  /* Shorter description — two lines max */
  .hero-desc {
    font-size: 13px;
    margin-bottom: 16px;
    line-height: 1.5;
    color: rgba(255,255,255,0.72);
    /* clamp to 2 lines */
    display: -webkit-box;
    -webkit-line-clamp: 2;
    line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }

  /* Trust pills stay on one row but shrink slightly */
  .trust-pills {
    gap: 6px;
    margin-bottom: 14px;
  }

  .trust-pill {
    padding: 5px 10px;
    font-size: 11px;
  }

  /* Search bar stacks on mobile: What / Where / full-width button */
  .search-card {
    flex-direction: column;
    align-items: stretch;
    padding: 6px;
    gap: 4px;
    margin-bottom: 14px;
    border-radius: 12px;
  }

  .search-divider {
    display: none;
  }

  .what-wrapper {
    border-bottom: 1px solid #f1f5f9;
  }

  .location-input {
    border-radius: 8px;
    padding: 10px 10px 10px 40px;
    font-size: 13px;
  }

  .what-input {
    padding-left: 40px;
  }

  .location-icon {
    left: 12px;
    width: 16px;
    height: 16px;
  }

  .what-icon {
    left: 12px;
  }

  .what-dropdown-scroll {
    max-height: 240px;
  }

  /* Keyboard hint is meaningless on touch */
  .what-ai-enter {
    display: none;
  }

  .search-btn {
    width: 100%;
    justify-content: center;
    padding: 10px 14px;
    font-size: 13px;
    border-radius: 8px;
    white-space: nowrap;
    flex-shrink: 0;
  }

  /* Stats row — compact inline */
  .hero-stats {
    display: flex;
    flex-wrap: nowrap;
    gap: 0;
    align-items: center;
  }
  .stat-item {
    padding: 0 12px 0 0;
  }
  .stat-num {
    font-size: 16px;
  }
  .stat-label {
    font-size: 10px;
  }
  .stat-divider {
    height: 24px;
    margin: 0 12px 0 0;
    display: block;
  }
}
</style>
